import React from 'react';
import { DateTime } from 'luxon';
import {
  Badge,
  BodyText,
  BoldBodyText,
  Currency,
  eFilterType,
  FilteredDataGrid,
  FlexRow,
  Grid,
  Notification,
  ToggleSwitch,
  Tooltip,
} from 'ui-toolkit';
import { IPageData } from 'types/rest/IPageData';
import { IEscrowActualIncomingCheckData } from '../../types/accounting/data/IEscrowActualIncomingCheckData';
import { IEscrowActualIncomingCheckQuery } from '../../types/accounting/query/IEscrowActualIncomingCheckQuery';
import { ITableChangeState } from 'react-bootstrap-table-next';
import { calculateRemainingSplitValue } from '../../utilities/accounting/calculateRemainingSplitValue';
import { IEscrowExpectedIncomingCheckData } from '../../types/accounting/data/IEscrowExpectedIncomingCheckData';
import { IncomingTransactionsNavigation } from './IncomingTransactionsNavigation';
import styled from 'styled-components';
import { IsMatchedFilter } from './IsMatchedFilter';
import { decleanDataGridNames } from './cleanDataGridNames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPennant } from '@fortawesome/pro-solid-svg-icons';
import { eMatchedQueryValue } from '../../types/accounting/eMatchedQueryValue';
import { AmountCents, BankAccountRow } from './SharedColumnDefinitions';

const RoomyGapGrid = styled(Grid)`
  gap: ${(props) => props.theme.spacing.spacing5};
`;

const ColumnThree = styled.div`
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing5};
  justify-items: end;
`;

export interface IActualIncomingChecksDataGridProps {
  data?: IPageData<IEscrowActualIncomingCheckData>;
  isQueryFetching: boolean;
  query: IEscrowActualIncomingCheckQuery;
  setSelectedId: (detail: string) => void;
  setQuery: (query: IEscrowActualIncomingCheckQuery) => void;
}

export const ActualIncomingChecksSearchTable: React.FC<IActualIncomingChecksDataGridProps> = ({
  data,
  isQueryFetching,
  query,
  setSelectedId,
  setQuery,
}) => {
  const isMatchedFilterItems: eMatchedQueryValue[] = [
    eMatchedQueryValue.UNMATCHED,
    eMatchedQueryValue.MATCHED,
    eMatchedQueryValue.MISMATCHED,
    eMatchedQueryValue.ALL,
  ];

  return (
    <>
      <RoomyGapGrid
        templateColumns={
          query.wellsFargoPaymentManagerReportType === 'Intraday' ? '1fr max-content max-content' : '1fr max-content'
        }
        alignItems="center"
      >
        <div>
          <IncomingTransactionsNavigation transactionType="checks" />
        </div>
        {query.wellsFargoPaymentManagerReportType === 'Intraday' && (
          <Notification type={'warning'}>
            <FlexRow>
              <BoldBodyText>Same Day Checks</BoldBodyText>
              <BodyText>Limited information available</BodyText>
            </FlexRow>
          </Notification>
        )}
        <ColumnThree>
          <ToggleSwitch
            id={'sameDay'}
            checked={query.wellsFargoPaymentManagerReportType === 'Intraday' || false}
            value=""
            onChange={(_, checked) =>
              setQuery({
                ...query,
                wellsFargoPaymentManagerReportType: checked ? 'Intraday' : 'PreviousDay',
                pageNumber: 1,
              })
            }
            label={'Same Day Checks'}
          />
          <IsMatchedFilter
            matched={query.matched}
            onChange={(matched) => setQuery({ ...query, matched, pageNumber: 1 })}
            items={isMatchedFilterItems}
          />
        </ColumnThree>
      </RoomyGapGrid>
      {data && (
        <FilteredDataGrid
          columns={[
            {
              dataField: 'completedAtDate',
              filterDefault: query.completedAtDate,
              filterElement: eFilterType.DateRange,
              filterPlaceholder: 'Completed At',
              formatter: (column: any) => DateTime.fromISO(column as string).toFormat('D'),
              headerStyle: { width: '145px' },
              text: 'Completed At',
              sort: true,
            },
            {
              dataField: 'checkIdentifier',
              filterDefault: query.checkIdentifier,
              filterElement: eFilterType.Text,
              filterPlaceholder: 'Check Identifier',
              headerStyle: { width: '150px' },
              text: 'Check Identifier',
            },
            {
              dataField: 'checkDetail',
              filterDefault: query.checkDetail,
              filterElement: eFilterType.Text,
              filterPlaceholder: 'Check Detail',
              headerStyle: { width: 'auto' },
              text: 'Check Detail',
              sort: true,
            },
            BankAccountRow,
            AmountCents(query.amountCents),
            {
              dataField: 'unmatchedAmountCents',
              filterDefault:
                typeof query.unmatchedAmountCents === 'number'
                  ? query.unmatchedAmountCents.toString()
                  : query.unmatchedAmountCents,
              filterElement: eFilterType.Currency,
              filterPlaceholder: 'Unmatched',
              formatter: (_, row) => {
                const { amountCents, expectedIncomingChecks: matches } = row as IEscrowActualIncomingCheckData;
                const remainingAmount = amountCents - matches.reduce((sum, { amountCents }) => sum + amountCents, 0);
                return <Currency value={remainingAmount} valueIsCents={true} />;
              },
              headerStyle: { width: '120px' },
              text: 'Unmatched',
            },
            {
              dataField: 'expectedIncomingChecks',
              formatter: (column, row) => {
                const fullyMatched =
                  calculateRemainingSplitValue(
                    (row && row.amountCents) || 0,
                    (column || []) as IEscrowExpectedIncomingCheckData[]
                  ) === 0;
                const partiallyMatched = !!(column as object[]).length && !fullyMatched;
                const mismatched =
                  row &&
                  row.expectedIncomingChecks &&
                  !!row.expectedIncomingChecks.length &&
                  row.accountNumber !== row.expectedIncomingChecks[0].escrowFile.depositEscrowBankAccount.accountNumber;
                return (
                  <>
                    {fullyMatched && <Badge intent={'primary'}>matched</Badge>}
                    {partiallyMatched && <Badge intent={'secondary'}>partial</Badge>}
                    {mismatched && (
                      <Tooltip placement={'bottom'} tooltipContent={<BodyText>Bank account does not match</BodyText>}>
                        <FontAwesomeIcon icon={faPennant} color={'red'} />
                      </Tooltip>
                    )}
                    {!fullyMatched && !partiallyMatched && <Badge intent={'danger'}>unmatched</Badge>}
                  </>
                );
              },
              headerStyle: { width: '110px' },
              text: 'Status',
            },
          ]}
          data={data.content}
          keyField="id"
          loading={isQueryFetching}
          onTableChange={(type: string, tableState: ITableChangeState<IEscrowActualIncomingCheckData>) => {
            const newFilters = Object.keys(tableState.filters).reduce(
              (p, key) => ({ ...p, [key]: tableState.filters[key].filterVal }),
              {}
            );

            setQuery({
              ...query,
              ...newFilters,
              pageNumber: tableState.page,
              pageSize: tableState.sizePerPage,
              sortField: tableState.sortField || undefined,
              sortOrder: tableState.sortOrder || undefined,
            });
          }}
          remote={true}
          selectRow={{
            mode: 'radio',
            hideSelectColumn: true,
            onSelect: (row) => setSelectedId(row.id),
            clickToSelect: true,
          }}
          pagination={{
            page: query.pageNumber || 1,
            sizePerPage: query.pageSize || 25,
            totalSize: data.totalElements,
          }}
          sort={{ dataField: decleanDataGridNames(query.sortField) || 'UNSET', order: query.sortOrder || 'asc' }}
        />
      )}
    </>
  );
};
